import type { NornClient, Schemas } from "./api"
import { run, scopeQuery } from "./api"
import type { Scope } from "./connection"

const connectorIds = ["erpnext", "gmail", "posthog", "cloudflare"] as const
const statuses = ["waiting_for_credential", "awaiting_consent", "ready", "syncing", "healthy", "error", "disabled"] as const
const credentialKeys: ReadonlySet<string> = new Set(["NORN_ERPNEXT_READONLY_TOKEN", "NORN_GMAIL_OAUTH_CLIENT_ID", "NORN_GMAIL_OAUTH_CLIENT_SECRET", "NORN_GMAIL_REFRESH_TOKEN", "NORN_POSTHOG_READONLY_API_KEY", "CLOUDFLARE_ANALYTICS_READ_TOKEN"])
export type ConnectorConnection = Schemas["ConnectionHealth"] & { connectorId: typeof connectorIds[number]; status: typeof statuses[number] }
export type ConnectorHealth = Omit<Schemas["ConnectorHealthResponse"], "connections"> & { connections: ConnectorConnection[] }
export class ConnectorProtocolError extends Error {}

/** UI protocol limits supplement the generated contract; they never define its DTO. */
function supportedConnection(item: Schemas["ConnectionHealth"]): item is ConnectorConnection {
  const counts = [item.records, item.checkpointVersion, item.requestCount]
  return connectorIds.some((id) => id === item.connectorId)
    && statuses.some((status) => status === item.status)
    && Array.isArray(item.missingCredentials) && item.missingCredentials.every((key) => credentialKeys.has(key))
    && counts.every((value) => Number.isSafeInteger(value) && value >= 0)
    && item.externalWriteCount === 0
}
export async function readConnectorHealth(client: NornClient, baseUrl: string, scope: Scope, signal?: AbortSignal): Promise<ConnectorHealth | null> {
  const result = await client.GET("/v1/connectors/health", { params: { query: scopeQuery(scope) }, signal, redirect: "error" })
  if (result.response.status === 404) return null
  const body = await run<Schemas["ConnectorHealthResponse"]>(baseUrl, async () => result)
  if (body?.version !== "norn.connector-health/1" || !Array.isArray(body.connections) || !body.connections.every((item) => item != null && supportedConnection(item))) throw new ConnectorProtocolError("Invalid connector health response")
  return { ...body, connections: body.connections.filter(supportedConnection) }
}
export async function startGmailConsent(client: NornClient, baseUrl: string, scope: Scope): Promise<string> {
  const body = await run<Schemas["GmailConsentResponse"]>(baseUrl, () => client.GET("/v1/connectors/gmail/oauth/start", { params: { query: scopeQuery(scope) }, redirect: "error" }))
  let url: URL
  try { url = new URL(body.authorizationUrl) }
  catch { throw new ConnectorProtocolError("Invalid Gmail authorization response") }
  if (url.protocol !== "https:" || url.hostname !== "accounts.google.com" || url.username || url.password) throw new ConnectorProtocolError("Invalid Gmail authorization URL")
  return url.href
}
